import {
  DestroyRef,
  Directive,
  ElementRef,
  inject,
  input,
  signal,
} from '@angular/core';

/** A section pinned within this many px of the container top counts as active. */
const TOP_THRESHOLD = 8;

/**
 * Scroll-spy for a vertical scroll container with labelled child sections.
 *
 * Tracks which section is pinned to the top and exposes it as `active()`; call
 * `scrollTo(id)` to jump to a section. While that smooth-scroll animates, the
 * spy is paused so it doesn't flip through every section in between — it resumes
 * once the user interacts with the container manually. Sections are matched by
 * a `data-*` attribute whose key is given by the `scrollSpyKey` input (default
 * `cat`, i.e. `[data-cat="<id>"]`).
 */
@Directive({
  selector: '[fcScrollSpy]',
  standalone: true,
})
export class ScrollSpyDirective {
  /** dataset key identifying each section, e.g. `cat` → `[data-cat]`. */
  readonly key = input('cat', { alias: 'scrollSpyKey' });

  /** id of the section currently pinned to the top of the container. */
  readonly active = signal('');

  private readonly el =
    inject<ElementRef<HTMLElement>>(ElementRef).nativeElement;
  private spying = true;
  private frame = 0;

  constructor() {
    const onScroll = () => this.onScroll();
    this.el.addEventListener('scroll', onScroll, { passive: true });

    const resumeSpying = () => {
      this.spying = true;
    };

    this.el.addEventListener('wheel', resumeSpying, { passive: true });
    this.el.addEventListener('touchstart', resumeSpying, { passive: true });
    this.el.addEventListener('pointerdown', resumeSpying, { passive: true });
    this.el.addEventListener('keydown', resumeSpying, { passive: true });

    inject(DestroyRef).onDestroy(() => {
      this.el.removeEventListener('scroll', onScroll);
      this.el.removeEventListener('wheel', resumeSpying);
      this.el.removeEventListener('touchstart', resumeSpying);
      this.el.removeEventListener('pointerdown', resumeSpying);
      this.el.removeEventListener('keydown', resumeSpying);
      cancelAnimationFrame(this.frame);
    });
  }

  /** Smooth-scroll a section to the top and mark it active immediately. */
  scrollTo(id: string): void {
    const target = this.section(id);
    if (!target) return;

    if (this.frame) {
      cancelAnimationFrame(this.frame);
      this.frame = 0;
    }

    this.active.set(id);
    this.spying = false;
    target.scrollIntoView({ block: 'start', behavior: 'smooth' });
  }

  /** Highlight the last section whose top has scrolled past the container top. */
  private onScroll(): void {
    if (!this.spying || this.frame) return;
    this.frame = requestAnimationFrame(() => {
      this.frame = 0;
      if (!this.spying) return;
      const top = this.el.getBoundingClientRect().top;
      let current = '';
      const sections = Array.from(
        this.el.querySelectorAll<HTMLElement>(`[data-${this.key()}]`),
      );
      for (const section of sections) {
        if (section.getBoundingClientRect().top - top > TOP_THRESHOLD) break;
        current = section.dataset[this.key()] ?? '';
      }
      if (current) this.active.set(current);
    });
  }

  private section(id: string): HTMLElement | null {
    return this.el.querySelector<HTMLElement>(`[data-${this.key()}="${id}"]`);
  }
}
